import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, TextStyle, View } from 'react-native';

import { AUTH_MODE } from '../../constants/Constants';
import { isEmail } from '../../utils/validation';
import Button from '../global/Button';
import Divider from '../global/Divider';
import RoundedButton from '../global/RoundedButton';
import Text from '../global/Text';
import { AuthContainerProps } from './AuthContainer';
import AuthField from './AuthField';
import AuthFormFooter from './AuthFormFooter';
import AuthFormHeader from './AuthFormHeader';
import AuthFreezeBanner from './AuthFreezeBanner';
import ToolTips from '../tooltips/ToolTips';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
export type AuthFormProps = Omit<AuthContainerProps, 'visibleTutorialModal' | 'freezeBanner'>;

export type AuthFormPropsExtend = AuthFormProps & {
  handleVisibleTutorialModal: any;
  onTogglePasswordVisibility: () => void;
  isPasswordVisible: boolean;
  freezeBanner: boolean;
  onSubmitRequestFederated: () => void;
};

type firstTimeFocused = {
  login: boolean;
  activateAccount: boolean;
};

interface AuthModeEnumInstance {
  key: AUTH_MODE;
  value: string;
}
interface AuthModeEnum {
  [key: string]: AuthModeEnumInstance;
}
/**
 * The form flow for Auth.
 */
const AuthForm = (props: AuthFormPropsExtend) => {
  const { t } = useTranslation(['auth', 'common']);
  const passwordInputRef: React.RefObject<any> = React.createRef();
  const [isActiveToolTips, setIsActiveToolTips] = useState<boolean>(false);
  const [isFirstTimeFocused, setIsFirstTimeFocused] = useState<firstTimeFocused>({
    login: false,
    activateAccount: false
  });

  const handleSubmitPressed = () => {
    if (props.authMode === AUTH_MODE.SIGN_UP) {
      return props.onSubmitSignup();
    } else if (props.authMode === AUTH_MODE.LOGIN) {
      if (!props._submitOnEnter && !!passwordInputRef?.current) {
        passwordInputRef.current!.focus();
        return props.onFocusMovedDown();
      }
      return props.onSubmitLogin();
    }
  };

  const _renderAuthHeader = (authMode: AUTH_MODE) => {
    const text: AuthModeEnum = {
      [AUTH_MODE.SIGN_UP]: t('auth:form.enterEmailPhone'),
      [AUTH_MODE.LOGIN]: t('auth:form.loginAccount'),
      [AUTH_MODE.RESET_PASSWORD]: t('auth:form.sendARequest')
    };
    const keyExist = authMode in text;
    return keyExist ? <Text style={styles.authHeader}>{text[authMode]}</Text> : null;
  };

  const tooltipsContent = () => {
    const instructions = [t('auth:form.loginInstruction1')];
    return (
      <View style={styles.tooltipContentContainer}>
        <Text style={styles.loginInstructionTitle}>{t('auth:form.loginInstructionTitle')}</Text>
        {instructions.map((loginInstruction, i) => (
          <Text key={i} style={styles.instruction}>
            {loginInstruction}
          </Text>
        ))}
      </View>
    );
  };

  const _renderAuthField = (authMode: string) => {
    const isValidationBottomError = props.formValidation.errorTextBottom !== '' && !props.formValidation.emailOrPhone;
    switch (authMode) {
      case AUTH_MODE.LOGIN:
        return (
          <React.Fragment>
            <ToolTips isActive={isActiveToolTips} content={tooltipsContent}>
              <AuthField
                testID="email-input"
                isValid={props.formValidation.emailOrPhone}
                disabled={props._isBusy}
                icon="blank"
                onFocus={() => {
                  if (!isFirstTimeFocused.login) {
                    setIsActiveToolTips(true);
                    setIsFirstTimeFocused((prevState: firstTimeFocused) => ({
                      ...prevState,
                      login: true
                    }));
                  } else {
                    setIsActiveToolTips(false);
                  }
                  props.onEmailTextInputFocused();
                }}
                onBlur={() => setIsActiveToolTips(false)}
                onChangeText={(text: string) => {
                  if (text.length > 0) {
                    setIsActiveToolTips(false);
                  }
                  props.onEmailOrPhoneChange(text);
                }}
                value={props.form.emailOrPhone}
                returnKeyType="next"
                blurOnSubmit={true}
                onSubmitEditing={handleSubmitPressed}
                placeholder={t('auth:form.emailOrPhone')}
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </ToolTips>

            {isValidationBottomError && !props.formValidation.isNeedAccessFederated ? (
              <View style={styles.errorTextWrapper}>
                <Text style={styles.errorText}>{props.formValidation.errorTextBottom}</Text>
              </View>
            ) : null}

            {props.formValidation.isNeedAccessFederated && props.formValidation.errorTextBottom ? (
              <View style={styles.requestAccessContainer}>
                <Icon style={styles.iconContainer} name="alert-circle" color="#FC4C5E" size={16} />
                <Text style={styles.errorText}>{props.formValidation.errorTextBottom}</Text>
              </View>
            ) : null}

            {shouldShowPasswordField && (
              <AuthField
                testID="password-input"
                autoFocus
                ref={passwordInputRef}
                isValid={props.formValidation.password}
                isPasswordVisible={props.isPasswordVisible}
                disabled={props._isBusy}
                icon="lock"
                value={props.form.password}
                blurOnSubmit={true}
                placeholder={t('auth:form.password')}
                keyboardType="default"
                secureTextEntry={!props.isPasswordVisible}
                onFocus={props.onPasswordTextInputFocused}
                onChangeText={props.onPasswordChange}
                onSubmitEditing={handleSubmitPressed}
                onTogglePasswordVisibility={props.onTogglePasswordVisibility}
              />
            )}
          </React.Fragment>
        );
      case AUTH_MODE.SIGN_UP:
        return (
          <ToolTips isActive={isActiveToolTips} content={tooltipsContent}>
            <AuthField
              isValid={props.formValidation.emailOrPhone}
              disabled={props._isBusy}
              icon="blank"
              onFocus={() => {
                if (!isFirstTimeFocused.activateAccount) {
                  setIsActiveToolTips(true);
                  setIsFirstTimeFocused((prevState: firstTimeFocused) => ({
                    ...prevState,
                    activateAccount: true
                  }));
                } else {
                  setIsActiveToolTips(false);
                }
                props.onEmailTextInputFocused();
              }}
              onBlur={() => setIsActiveToolTips(false)}
              onChangeText={(text: string) => {
                if (text.length > 0) {
                  setIsActiveToolTips(false);
                }
                props.onEmailOrPhoneChange(text);
              }}
              value={props.form.emailOrPhone}
              returnKeyType="next"
              blurOnSubmit={true}
              placeholder={t('auth:form.emailOrPhone')}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </ToolTips>
        );
      case AUTH_MODE.RESET_PASSWORD:
        return (
          <AuthField
            isValid={props.formValidation.email}
            disabled={props._isBusy}
            icon="email"
            onFocus={props.onEmailTextInputFocused}
            onChangeText={props.onEmailChange}
            value={props.form.email}
            returnKeyType="next"
            blurOnSubmit={true}
            onSubmitEditing={handleSubmitPressed}
            placeholder={t('auth:form.email')}
            keyboardType="email-address"
            autoCapitalize="none"
          />
        );
    }
  };

  const validEmailOrPhone = isEmail(props.form.emailOrPhone);
  // TEMP (hardcoded): federated login disabled — always show the password field on the login
  // screen so the user enters email/username + password in one step. Restore the original
  // condition below to bring back the two-step "Continue → reveal password" flow:
  //   props.authMode === AUTH_MODE.LOGIN && (props.showPasswordField || props.hasPendingCreds || !props.isFederatedAuth)
  const shouldShowPasswordField = props.authMode === AUTH_MODE.LOGIN;
  const loginPrimaryLabel = shouldShowPasswordField ? t('auth:form.login') : t('common:continue');

  return (
    <View style={styles.formWrapper}>
      <AuthFormHeader authMode={props.authMode} language={props.language} />
      <View style={styles.authContainer}>
        {_renderAuthHeader(props.authMode)}
        {_renderAuthField(props.authMode)}
        {props.hasPendingCreds && props.formValidation.errorTextBottom ? (
          <View style={styles.requestAccessContainer}>
            <Icon style={styles.iconContainer} name="alert-circle" color="#FC4C5E" size={16} />
            <Text style={styles.errorText}>{props.formValidation.errorTextBottom}</Text>
          </View>
        ) : null}
        {props.formValidation.errorText !== '' ? (
          <View style={styles.requestAccessContainer}>
            <Icon style={styles.iconContainer} name="alert-circle" color="#FC4C5E" size={16} />
            <Text style={styles.errorText}>{props.formValidation.errorText}</Text>
          </View>
        ) : null}
        {props.formValidation.errorText === '' && props._capsWarning ? (
          <Text style={styles.errorText}>{t('auth:form.capsLock')}</Text>
        ) : null}
        {props._isBusy ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color="#3cd070" />
          </View>
        ) : null}
        {!props._isBusy && props.authMode === AUTH_MODE.LOGIN && !props.formValidation.isNeedAccessFederated && (
          <View style={styles.actionContainer}>
            <Button
              testID="button-submit"
              disabled={!props.form.emailOrPhone.trim() || props._isBusy}
              onPress={props.onSubmitLogin}
              title={loginPrimaryLabel}
              style={styles.button}
            />
          </View>
        )}
        {!props._isBusy && props.formValidation.isNeedAccessFederated && props.authMode === AUTH_MODE.LOGIN && (
          <View style={styles.actionContainer}>
            <RoundedButton
              onPress={props.onSubmitRequestFederated}
              title={t('auth:form.requestButton')}
              backgroundColor="#56C768"
              style={styles.button}
              inverse={true}
            />
          </View>
        )}
        {props.authMode === AUTH_MODE.SIGN_UP && props._emailSentSuccess ? (
          <Text style={{ ...(styles.emailSentNotification as TextStyle), color: 'green' }}>
            {t('auth:form.checkEmail')}
          </Text>
        ) : null}
        {!props._isBusy && props.authMode === AUTH_MODE.SIGN_UP && (
          <View style={styles.actionContainer}>
            <Button
              disabled={!validEmailOrPhone || props._isBusy || props._emailSentSuccess}
              onPress={props.onSubmitSignup}
              title={t('auth:form.verifyAccount')}
              style={styles.button}
            />
          </View>
        )}
        {!props._isBusy && props.authMode === AUTH_MODE.RESET_PASSWORD && (
          <View style={styles.actionContainer}>
            <Button
              disabled={!props.form.email || props._isBusy}
              onPress={props.onSubmitPasswordReset}
              title={t('auth:form.submitChangePassword')}
              style={styles.button}
            />
          </View>
        )}
        {props.freezeBanner ? <AuthFreezeBanner /> : null}

        {props.authMode === AUTH_MODE.LOGIN &&
          !props.formValidation.isNeedAccessFederated &&
          shouldShowPasswordField && (
            <Text style={styles.forgotPasswordLink} onPress={props.onChangeSectionForgotPassword}>
              {t('auth:form.forgotPassword')}
            </Text>
          )}
        {props.authMode === AUTH_MODE.RESET_PASSWORD && (
          <Text style={[styles.forgotPasswordLink, styles.forgotPasswordFooter]} onPress={props.onChangeSectionLogin}>
            {t('auth:form.backToLogin')}
          </Text>
        )}
        {props.authMode !== AUTH_MODE.RESET_PASSWORD ? <Divider /> : null}

        <AuthFormFooter
          authMode={props.authMode}
          language={props.language}
          onChangeSectionLogin={() => {
            setIsActiveToolTips(false);
            props.onChangeSectionLogin();
          }}
          onChangeSectionSignup={() => {
            setIsActiveToolTips(false);
            props.onChangeSectionSignup();
          }}
          onContactWhatsAppSupport={props.onContactWhatsAppSupport}
          handleVisibleTutorialModal={props.handleVisibleTutorialModal}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formWrapper: {
    width: '100%',
    alignItems: 'center',
    paddingTop: 4,
    paddingBottom: 8
  },
  requestAccessContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10
  },
  iconContainer: {
    marginRight: 6,
    marginTop: 2
  },
  authContainer: {
    width: 456,
    maxWidth: '92%',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E4E7EC',
    paddingTop: 24,
    paddingBottom: 28,
    paddingHorizontal: 24,
    shadowColor: '#101828',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.08,
    shadowRadius: 28,
    elevation: 8
  },
  authHeader: {
    marginTop: 2,
    marginHorizontal: 20,
    marginBottom: 18,
    textAlign: 'center',
    color: '#475467',
    fontSize: 14
  },
  errorTextWrapper: {
    width: '100%',
    marginBottom: 12,
    marginTop: -8,
    marginLeft: 8
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    textAlign: 'left',
    color: '#FA6161',
    lineHeight: 18
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: 42.5,
    marginVertical: 10
  },
  actionContainer: {
    width: '100%',
    alignSelf: 'stretch',
    alignItems: 'stretch'
  },
  button: {
    width: '100%',
    marginTop: 4,
    marginBottom: 12,
    borderRadius: 12,
    paddingVertical: 11,
    backgroundColor: '#16A34A'
  },
  forgotPasswordLink: {
    textAlign: 'center',
    fontWeight: '600',
    fontSize: 14,
    color: '#16A34A',
    marginTop: 2
  },
  forgotPasswordFooter: {
    marginBottom: 30
  },
  emailSentNotification: {
    textAlign: 'center'
  },
  tooltipContentContainer: {
    padding: 14
  },
  loginInstructionTitle: {
    fontWeight: 'bold',
    fontSize: 14
  },
  instruction: {
    fontSize: 14
  }
});

export default AuthForm;
